import * as THREE from 'three';
import { BOMB_RULES } from '../modes/rules.js';

const _v = new THREE.Vector3();

/**
 * The bomb.
 *
 * States: `carried` -> `dropped` -> `carried` ... -> `planted` -> `defused`
 * or `exploded`. Only one exists per round, so nothing here is pooled — but the
 * mesh and materials are built once and reused across rounds rather than
 * rebuilt, because allocating a material mid-round compiles a shader and that
 * is a visible hitch.
 *
 * Detonation reuses `Game.detonate`, which already does line-of-sight-checked
 * splash damage for the RPG and grenades.
 */
export class Bomb {
  constructor(game) {
    this.game = game;
    this.state = 'carried';
    this.carrier = null;         // player object or an Enemy
    this.fuse = 0;
    this.site = null;            // label of the site it was planted in
    this.position = new THREE.Vector3();
    this.body = null;            // physics body while dropped

    this._beepAt = 0;
    this._blink = 0;
    this._build();
  }

  _build() {
    const g = new THREE.Group();

    const shell = new THREE.Mesh(
      new THREE.BoxGeometry(0.30, 0.14, 0.20),
      new THREE.MeshStandardMaterial({ color: 0x2c2f24, roughness: 0.72, metalness: 0.15 })
    );
    g.add(shell);

    // Taped charge blocks — reads as C4 rather than a crate at a glance.
    for (let i = -1; i <= 1; i++) {
      const brick = new THREE.Mesh(
        new THREE.BoxGeometry(0.085, 0.10, 0.155),
        new THREE.MeshStandardMaterial({ color: 0xb9ac86, roughness: 0.86 })
      );
      brick.position.set(i * 0.093, 0.045, 0);
      g.add(brick);
    }

    // Panel + LED. The LED is the state read at distance, so it is emissive and
    // deliberately unlit by the scene.
    const panel = new THREE.Mesh(
      new THREE.BoxGeometry(0.10, 0.055, 0.02),
      new THREE.MeshStandardMaterial({ color: 0x14161a, roughness: 0.5 })
    );
    panel.position.set(0, 0.075, 0.10);
    g.add(panel);

    this.led = new THREE.Mesh(
      new THREE.SphereGeometry(0.017, 10, 8),
      new THREE.MeshBasicMaterial({ color: 0xff2a12, toneMapped: false })
    );
    this.led.position.set(0, 0.105, 0.10);
    g.add(this.led);

    // A short antenna gives the silhouette something vertical to catch the eye.
    const ant = new THREE.Mesh(
      new THREE.CylinderGeometry(0.005, 0.005, 0.16, 6),
      new THREE.MeshStandardMaterial({ color: 0x1a1c1e, roughness: 0.6 })
    );
    ant.position.set(0.10, 0.15, -0.05);
    g.add(ant);

    g.traverse((o) => { if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; } });
    g.visible = false;
    this.mesh = g;
    this.game.engine.scene.add(g);
  }

  /* ------------------------------------------------------------ state --- */

  giveTo(carrier) {
    this._releaseBody();
    this.carrier = carrier;
    this.state = 'carried';
    this.mesh.visible = false;
  }

  /** Drop at a position with a small toss — used when the carrier dies. */
  drop(at, velocity = null) {
    this._releaseBody();
    this.carrier = null;
    this.state = 'dropped';
    this.position.copy(at);
    this.mesh.position.copy(at);
    this.mesh.visible = true;

    this.body = this.game.physics.addBody({
      position: this.mesh.position,
      quaternion: this.mesh.quaternion,
      velocity: velocity
        ? velocity.clone()
        : new THREE.Vector3((Math.random() - 0.5) * 1.6, 1.4, (Math.random() - 0.5) * 1.6),
      radius: 0.16,
      drag: 0.9,
      restitution: 0.18,
      life: 1e9,          // the bomb never expires on its own
      spin: {
        axis: new THREE.Vector3(Math.random(), Math.random(), Math.random()).normalize(),
        rate: 4,
      },
      onExpire: () => {},
    });
  }

  plant(at, siteLabel) {
    this._releaseBody();
    this.carrier = null;
    this.state = 'planted';
    this.site = siteLabel;
    this.fuse = BOMB_RULES.bombTimer;
    this.position.copy(at);
    this.mesh.position.copy(at);
    this.mesh.quaternion.identity();
    this.mesh.visible = true;
    this._beepAt = 0;
    this.game.audio?.bombPlant?.(at);
    this.onPlanted?.(siteLabel);
  }

  defuse() {
    if (this.state !== 'planted') return false;
    this.state = 'defused';
    this.led.material.color.setHex(0x2bd44a);
    this.game.audio?.bombDefused?.(this.position);
    this.onDefused?.();
    return true;
  }

  explode() {
    if (this.state === 'exploded') return;
    this.state = 'exploded';
    this.mesh.visible = false;
    const R = BOMB_RULES.explodeRadius;
    this.game.detonate(this.position.clone(), R, BOMB_RULES.explodeDamage);
    // A second, larger puff a beat later so it reads bigger than an RPG.
    setTimeout(() => this.game.effects?.explosion?.(this.position.clone(), R * 1.35), 110);
    this.game.player?.addTrauma?.(1);
    this.onExploded?.();
  }

  /** Return the bomb to a carrier's hands at round start. */
  reset(carrier) {
    this._releaseBody();
    this.state = 'carried';
    this.carrier = carrier;
    this.site = null;
    this.fuse = 0;
    this._blink = 0;
    this._beepAt = 0;
    this.led.material.color.setHex(0xff2a12);
    this.mesh.visible = false;
    // Belt and braces: park the mesh on the new carrier rather than leaving it
    // sitting at last round's crater. Hidden geometry left at a stale position
    // is how a "the bomb is still there next round" report starts.
    if (carrier) {
      this.position.copy(carrier.isEnemy ? carrier.root.position : carrier.position);
      this.mesh.position.copy(this.position);
    }
    this.mesh.quaternion.identity();
  }

  _releaseBody() {
    if (this.body) {
      this.game.physics.removeBody(this.body);
      this.body = null;
    }
  }

  /* ----------------------------------------------------------- update --- */

  update(dt) {
    if (this.state === 'carried') {
      // Ride the carrier so a dropped-on-death bomb lands in the right place.
      const c = this.carrier;
      if (c) this.position.copy(c.isEnemy ? c.root.position : c.position);
      return;
    }

    if (this.state === 'dropped') {
      this.position.copy(this.mesh.position);
      // Walk-over pickup, as in CS — no keypress.
      const t = this.game.pickupCandidate?.(this.position);
      if (t) {
        this.giveTo(t);
        this.game.audio?.reload?.('in');
      }
      return;
    }

    if (this.state !== 'planted') return;

    this.fuse -= dt;
    this._plantedFx(dt);
    if (this.fuse <= 0) this.explode();
  }

  /**
   * Drive the bomb from the server instead of from its own logic.
   *
   * Online this object must not decide anything. It used to run its full state
   * machine alongside the server's — a second bomb, carried by nobody, that no
   * other player could see and that the server had never heard of. Same class
   * of bug as the bots that kept spawning in multiplayer: a local simulation
   * quietly shadowing the authoritative one.
   *
   * What survives is the presentation, because that is genuinely this
   * machine's job: where the mesh sits, the LED, and the beep cadence — which
   * has to run locally or it would arrive in 20 Hz steps.
   *
   * @param {object} b  the snapshot's bomb block: { s, c, f, p, site }
   */
  syncNet(b, dt) {
    if (!b) return;
    this.state = b.s;
    this.site = b.site ?? null;
    this.fuse = b.f ?? 0;

    if (b.s === 'planted' && Array.isArray(b.p)) {
      this.position.set(b.p[0], b.p[1], b.p[2]);
      this.mesh.position.copy(this.position);
      this.mesh.quaternion.identity();
      this.mesh.visible = true;
      this._plantedFx(dt);
      return;
    }

    // Carried, defused or gone. There is no dropped bomb online — the server
    // never puts one on the floor — so anything that is not planted is simply
    // not in the world.
    this.mesh.visible = false;
  }

  /** The parts of a planted bomb that are pure presentation. */
  _plantedFx(dt) {
    // Beep cadence accelerates toward detonation; the LED blinks on the same
    // clock so sight and sound agree.
    const k = 1 - Math.max(0, this.fuse) / BOMB_RULES.bombTimer;
    const interval = THREE.MathUtils.lerp(1.0, 0.09, k * k);
    this._beepAt -= dt;
    if (this._beepAt <= 0) {
      this._beepAt = interval;
      this._blink = 1;
      this.game.audio?.bombBeep?.(this.position, k);
    }
    this._blink = Math.max(0, this._blink - dt * 9);
    this.led.material.color.setRGB(0.35 + this._blink * 0.65, this._blink * 0.16, 0.05);
  }

  /** Distance from `pos` to the bomb, or Infinity when it is being carried. */
  distanceTo(pos) {
    if (this.state === 'carried') return Infinity;
    return this.position.distanceTo(pos);
  }
}
